export type LateCommandPolicy = "drop" | "apply-immediate" | "reschedule";

export interface LateCommandPolicyConfig {
  readonly policy: LateCommandPolicy;
  readonly thresholdMs: number;
  readonly rescheduleDelayMs: number;
}

export interface SchedulerClock {
  nowMs(): number;
  setTimeout(callback: () => void, delayMs: number): number;
  clearTimeout(timerId: number): void;
}

export interface SchedulerDecision {
  readonly kind: "scheduled" | "applied-immediately" | "dropped" | "deduped";
  readonly commandId: string;
  readonly delayMs: number;
  readonly latenessMs: number;
  readonly policy: LateCommandPolicy;
}

interface PendingCommand {
  readonly timerId: number;
}

function resolveLateCommand(
  executeAtMs: number,
  nowMs: number,
  config: LateCommandPolicyConfig,
): { kind: "schedule" | "apply-now" | "drop"; delayMs: number; latenessMs: number } {
  const delayMs = executeAtMs - nowMs;
  if (delayMs >= 0) {
    return { kind: "schedule", delayMs, latenessMs: 0 };
  }
  const latenessMs = Math.abs(delayMs);
  if (latenessMs <= config.thresholdMs) {
    return { kind: "schedule", delayMs: 0, latenessMs };
  }
  if (config.policy === "drop") {
    return { kind: "drop", delayMs: 0, latenessMs };
  }
  if (config.policy === "apply-immediate") {
    return { kind: "apply-now", delayMs: 0, latenessMs };
  }
  return {
    kind: "schedule",
    delayMs: Math.max(0, config.rescheduleDelayMs),
    latenessMs,
  };
}

export class DeterministicCommandScheduler {
  private readonly pendingByCommandId = new Map<string, PendingCommand>();
  private readonly appliedCommandIds = new Set<string>();
  private readonly maxAppliedEntries: number;
  private readonly clock: SchedulerClock;
  private readonly lateConfig: LateCommandPolicyConfig;

  constructor(options: {
    readonly clock: SchedulerClock;
    readonly lateConfig: LateCommandPolicyConfig;
    readonly maxAppliedEntries: number;
  }) {
    this.clock = options.clock;
    this.lateConfig = options.lateConfig;
    this.maxAppliedEntries = options.maxAppliedEntries;
  }

  schedule(commandId: string, executeAtMs: number, apply: () => void): SchedulerDecision {
    if (this.appliedCommandIds.has(commandId) || this.pendingByCommandId.has(commandId)) {
      return {
        kind: "deduped",
        commandId,
        delayMs: 0,
        latenessMs: 0,
        policy: this.lateConfig.policy,
      };
    }

    const nowMs = this.clock.nowMs();
    const lateResolution = resolveLateCommand(executeAtMs, nowMs, this.lateConfig);
    if (lateResolution.kind === "drop") {
      this.markApplied(commandId);
      return {
        kind: "dropped",
        commandId,
        delayMs: 0,
        latenessMs: lateResolution.latenessMs,
        policy: this.lateConfig.policy,
      };
    }
    if (lateResolution.kind === "apply-now") {
      this.markApplied(commandId);
      apply();
      return {
        kind: "applied-immediately",
        commandId,
        delayMs: 0,
        latenessMs: lateResolution.latenessMs,
        policy: this.lateConfig.policy,
      };
    }

    const timerId = this.clock.setTimeout(() => {
      this.pendingByCommandId.delete(commandId);
      if (this.appliedCommandIds.has(commandId)) {
        return;
      }
      this.markApplied(commandId);
      apply();
    }, lateResolution.delayMs);
    this.pendingByCommandId.set(commandId, { timerId });
    return {
      kind: "scheduled",
      commandId,
      delayMs: lateResolution.delayMs,
      latenessMs: lateResolution.latenessMs,
      policy: this.lateConfig.policy,
    };
  }

  cancelAll(): void {
    for (const pending of this.pendingByCommandId.values()) {
      this.clock.clearTimeout(pending.timerId);
    }
    this.pendingByCommandId.clear();
  }

  resetAppliedCache(): void {
    this.appliedCommandIds.clear();
  }

  private markApplied(commandId: string): void {
    this.appliedCommandIds.add(commandId);
    while (this.appliedCommandIds.size > this.maxAppliedEntries) {
      const oldest = this.appliedCommandIds.values().next().value;
      if (oldest === undefined) {
        break;
      }
      this.appliedCommandIds.delete(oldest);
    }
  }
}
